import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { Save, RefreshCw } from "lucide-react";
import socket from "../../assets/socket";
const base_url = process.env.REACT_APP_API_URL;

/* ================= RESULT LIST ================= */
const numbers = [
  ...Array.from({ length: 99 }, (_, i) => String(i + 1).padStart(2, "0")),
  "00",
];
const andarMap = ["A0", "A1", "A2", "A3", "A4", "A5", "A6", "A7", "A8", "A9"];
const baharMap = ["B0", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "B8", "B9"];

const getTodayDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const istDate = new Date(now.getTime() - offset * 60 * 1000);
  return istDate.toISOString().split("T")[0]; // YYYY-MM-DD
};

const AdminManageMatka = () => {
  const { marketId } = useParams();
  const auth = localStorage.getItem("admin_token");

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    !(window.innerWidth < 768),
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [date, setDate] = useState();
  const [gameId, setGameId] = useState("");
  const [game, setGame] = useState(null);
  const [summary, setSummary] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalBets, setTotalBets] = useState(0);
  const [result, setResult] = useState("");

  const [bets, setBets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* Resize */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsSidebarOpen(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* Fetch Game */
  const fetchGame = async () => {
    try {
      let url = `${base_url}/api/admin/matka/current-game?market_id=${marketId}`;

      if (date) {
        url += `&date=${date}`;
      } else if (gameId) {
        url += `&game_id=${gameId}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: auth },
      });

      if (res.data.success) {
        setGame(res.data.game);
        setGameId(res.data.game.game_id);
        setSummary(res.data.summary || []);
        setTotalAmount(res.data.totalAmount || 0);
        setTotalBets(res.data.totalBets || 0);
      } else {
        toast.error(res.data.message || "No game available");
      }
    } catch {
      toast.error("Failed to load game");
    }
  };

  /* Fetch Bets */
  const fetchBets = async (p = 1) => {
    try {
      const res = await axios.get(
        `${base_url}/api/matka/user/bet-history?market_id=${marketId}&game_id=${gameId}&page=${p}&limit=10`,
      );

      if (res.data.success) {
        setBets(res.data.bets);
        setTotalPages(res.data.pagination.totalPages);
        setPage(p);
      }
    } catch {
      toast.error("Failed to load bets");
    }
  };

  useEffect(() => {
    fetchGame();
  }, [marketId]);

  useEffect(() => {
    fetchBets(1);
  }, [gameId]);

  /* Set Result */
  const setGameResult = async () => {
    if (!result) return toast.error("Select result");
    try {
      const res = await axios.post(
        `${base_url}/api/admin/matka/market/result`,
        { market_id: marketId, game_id: gameId, result },
        { headers: { Authorization: auth } },
      );
      if (res.data.success) {
        toast.success("Result declared");
        setResult("");
        fetchGame();
        fetchBets(1);
      }
    } catch {
      toast.error("Result update failed");
    }
  };

  useEffect(() => {
    socket.on("matka_bet_by_user", () => {
      fetchGame();
      fetchBets(1);
    });

    return () => {
      socket.off("matka_bet_by_user");
    };
  }, []);

  useEffect(() => {
    fetchGame();
    // fetchBets(1);
  }, [date]);

  const betMap = useMemo(() => {
    const map = {};
    summary.forEach((s) => {
      map[s.bet_value] = s.totalAmount;
    });
    return map;
  }, [summary]);

  const getColumnTotal = (col) => {
    let total = 0;

    for (let row = 0; row < 10; row++) {
      const num = String(row * 10 + (col + 1)).padStart(2, "0");
      total += betMap[num] || 0;
    }

    return total;
  };

  const isAndarBahar = (num) =>
    andarMap.includes(num) || baharMap.includes(num);

  if (!game) return <div className="p-5 text-center">Loading...</div>;

  return (
    <div className="wrapper">

      <div
        className={`content-wrapper ${
          !isMobile && isSidebarOpen ? "" : "open-content-wrapper"
        }`}
      >
        <div className="page-content px-3">
          <div className="d-flex justify-content-between mb-3 flex-wrap align-items-center">
            <h4>
              Matka – <span className="text-primary">{game.market_name}</span>
            </h4>
            <div className="d-flex gap-5">
              <input
                type="date"
                className="btn"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />

              <button
                className="btn btn-outline-primary btn-sm"
                onClick={fetchGame}
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
          </div>

          {/* GAME RESULT DECLEARE */}
          <div className="mb-4">
            <h5>
              Current Game Result:{" "}
              {game.result ? (
                <span className="text-success">{game.result}</span>
              ) : (
                <span className="text-danger">Not Declared</span>
              )}
            </h5>
          </div>

          {/* STATS */}
          <div className="row mb-0 mb-md-5">
            <div className="col-md-6">
              <div className="card p-3">
                <h6>Total Bet Amount</h6>
                <h3>₹{totalAmount}</h3>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card p-3">
                <h6>Total Bets</h6>
                <h3>{totalBets}</h3>
              </div>
            </div>
          </div>

          {/* SET RESULT (ONLY BEFORE DECLARATION) */}
          {!game.result && (
            <div
              className="card mb-4"
              style={{ maxWidth: "375px", margin: "0 auto" }}
            >
              <div className="card-header">
                <h5>Set Result</h5>
              </div>
              <div className="card-body" style={{ background: "#eaf6ff" }}>
                <div className="number-grid jantri-grid">
                  {Array.from({ length: 10 }).map((_, rowIndex) => {
                    const rowNums = numbers.slice(
                      rowIndex * 10,
                      rowIndex * 10 + 10,
                    );
                    const rowTotal = rowNums.reduce(
                      (sum, n) => sum + (betMap[n] || 0),
                      0,
                    );

                    return (
                      <React.Fragment key={rowIndex}>
                        {rowNums.map((num) => (
                          <div
                            key={num}
                            className={`number-btn 
                              ${result === num ? "active" : ""} 
                              ${isAndarBahar(num) ? "disabled-box" : ""}
                            `}
                            onClick={() => {
                              if (isAndarBahar(num)) {
                                toast.error("Andar / Bahar result not allowed");
                                return;
                              }
                              setResult(num);
                            }}
                          >
                            {num}
                            {betMap[num] && (
                              <span className="bet-amount">₹{betMap[num]}</span>
                            )}
                          </div>
                        ))}

                        <div className="row-total-box">{rowTotal || ""}</div>
                      </React.Fragment>
                    );
                  })}

                  {/* COLUMN TOTALS */}
                  {/* ANDAR ROW */}
                  {andarMap.map((num) => (
                    <div
                      key={num}
                      className={`number-btn disabled-box`}
                      onClick={() => toast.error("Andar result not allowed")}
                    >
                      {num}
                      {betMap[num] && (
                        <span className="bet-amount">₹{betMap[num]}</span>
                      )}
                    </div>
                  ))}
                  <div className="row-total-box">
                    {andarMap.reduce((s, n) => s + (betMap[n] || 0), 0)}
                  </div>

                  {/* BAHAR ROW */}
                  {baharMap.map((num) => (
                    <div
                      key={num}
                      className={`number-btn disabled-box`}
                      onClick={() => toast.error("Bahar result not allowed")}
                    >
                      {num}
                      {betMap[num] && (
                        <span className="bet-amount">₹{betMap[num]}</span>
                      )}
                    </div>
                  ))}
                  <div className="row-total-box">
                    {baharMap.reduce((s, n) => s + (betMap[n] || 0), 0)}
                  </div>

                  {/* COLUMN TOTALS */}
                  {Array.from({ length: 10 }).map((_, col) => (
                    <div key={col} className="row-total-box">
                      {getColumnTotal(col) || ""}
                    </div>
                  ))}

                  {/* GRAND TOTAL */}
                  <div className="row-total-box">
                    {Object.values(betMap).reduce((a, b) => a + b, 0)}
                  </div>
                </div>

                <button className="btn btn-success" onClick={setGameResult}>
                  <Save size={14} /> Save Result
                </button>
              </div>
            </div>
          )}

          {/* USER BET HISTORY (ONLY AFTER RESULT) */}
          <div className="card">
            <div className="card-header">
              <h5>User Bet History</h5>
            </div>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Number</th>
                    <th>Bet</th>
                    <th>Bet Commission</th>
                    <th>Win</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length > 0 ? (
                    bets.map((b, index) => (
                      <tr key={b.id}>
                        <td>{index + 1}</td>
                        <td>{b.phone}</td>
                        <td>{b.bet_value}</td>
                        <td>₹{b.bet_amount}</td>
                        <td>₹{b.bet_commission}</td>
                        <td>₹{b.win_amount ?? 0}</td>
                        <td>
                          {b.status === 1
                            ? "WIN"
                            : b.status === 2
                              ? "LOSE"
                              : "PENDDING"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        Bet not available!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-center align-items-center gap-2 p-2">
              <button
                disabled={page <= 1}
                onClick={() => fetchBets(page - 1)}
                className="btn btn-sm btn-outline-primary"
              >
                Prev
              </button>
              <span>
                Page {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => fetchBets(page + 1)}
                className="btn btn-sm btn-outline-primary"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .result-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(60px,1fr));gap:10px}
        .result-box{border:2px solid #ddd;padding:12px;border-radius:8px;font-weight:600}
        .result-box.active{background:#1f2d3d !important;color:#fff; display: block;}
        .result-box {
          border: 2px solid #ddd;
          padding: 10px;
          border-radius: 10px;
          text-align: center;
          cursor: pointer;
          background: #f9f9f9;
          transition: 0.2s;
        }

        .result-box:hover {
          background: #eef3ff;
        }

        .result-box.active {
          background: #0d6efd;
          color: white;
        }

        .res-num {
          font-size: 18px;
          font-weight: bold;
        }

        .res-amount {
          color: #28a745;
          font-size: 14px;
        }

        .res-count {
          font-size: 12px;
          color: #555;
        }

        .res-empty {
          font-size: 14px;
          color: #aaa;
        }
          .number-btn.active {
            background: #ffc107 !important;
            color: #000 !important;
        }
        .disabled-box {
          opacity: 0.6;
          cursor: not-allowed;
        }

      `}</style>
    </div>
  );
};

export default AdminManageMatka;
